import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import {
  Key, Activity, TestTube, FileText, RefreshCw, Copy, CheckCircle,
  XCircle, AlertTriangle, Send, ChevronDown, ChevronUp, Building2,
  Clock, Zap, Eye, EyeOff, Plus, ToggleLeft, ToggleRight, Package,
  AlertCircle, Search
} from 'lucide-react';
import './AdminPartnerAPI.css';

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

interface Partner {
  id: number;
  name: string;
  city: string;
  state: string;
  key_count: number;
  active_key_count: number;
  total_requests: number;
  last_api_call: string | null;
  has_api_access: boolean;
}

interface DashboardSummary {
  active_partners: number;
  active_keys: number;
  requests_24h: number;
  error_rate_24h: number;
}

interface DashboardData {
  partners: Partner[];
  summary: DashboardSummary;
}

interface APILog {
  id: number;
  partner_name: string;
  partner_id: number | null;
  endpoint: string;
  method: string;
  request_body: Record<string, any> | null;
  response_status: number;
  response_time_ms: number;
  error_message: string | null;
  ip_address: string;
  created_at: string;
}

interface APIKey {
  id: number;
  name: string;
  key?: string;
  key_type: string;
  masked_key: string;
}

interface NewKeyResponse {
  success: boolean;
  api_key: APIKey;
  message: string;
}

interface TestResult {
  success: boolean;
  request: {
    method: string;
    endpoint: string;
    payload: Record<string, any>;
  };
  response: {
    status: number;
    body: Record<string, any>;
    duration_ms: number;
  };
}

interface APIEndpoint {
  name: string;
  method: string;
  path: string;
  description: string;
  parameters?: Array<{ name: string; type: string; description: string }>;
  request_example?: Record<string, any>;
  response_example: Record<string, any>;
  curl_example: string;
}

interface APIDocs {
  title: string;
  version: string;
  base_url: string;
  authentication: {
    type: string;
    header: string;
    format: string;
    example: string;
  };
  endpoints: APIEndpoint[];
}

interface InventoryItem {
  id: number;
  sku: string;
  name: string;
  quantity: number;
  low_stock_threshold: number;
  is_low_stock: boolean;
  last_updated: string;
  feed_formula?: {
    id: number;
    code: string;
    name: string;
  };
}

interface PartnerInventory {
  partner_id: number;
  partner_name: string;
  partner_city: string;
  partner_state: string;
  items: InventoryItem[];
  total_items: number;
  low_stock_count: number;
}

interface InventoryData {
  partners: PartnerInventory[];
  sync_summary: Record<string, { sync_count: number; last_sync: string | null }>;
  summary: {
    partners_with_inventory: number;
    total_items: number;
    total_low_stock: number;
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const AdminPartnerAPI: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'inventory' | 'logs' | 'tester' | 'docs'>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [expandedPartner, setExpandedPartner] = useState<number | null>(null);

  // Inventory state
  const [inventoryData, setInventoryData] = useState<InventoryData | null>(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryFilter, setInventoryFilter] = useState({ partner_id: '', low_stock: false, search: '' });
  const [expandedInventoryPartner, setExpandedInventoryPartner] = useState<number | null>(null);
  // Server search: input holds instant local state; the debounced value (400ms) is synced
  // into inventoryFilter.search so fetchInventory (keyed on the filter) fires once per pause.
  const { value: inventorySearchInput, setValue: setInventorySearchInput, debounced: debouncedInventorySearch } =
    useDebouncedSearch('', 400);
  useEffect(() => {
    setInventoryFilter(f => (f.search === debouncedInventorySearch ? f : { ...f, search: debouncedInventorySearch }));
  }, [debouncedInventorySearch]);

  // Logs state
  const [logs, setLogs] = useState<APILog[]>([]);
  const [logsFilter, setLogsFilter] = useState({ partner_id: '', status: '', endpoint: '' });
  const [logsLoading, setLogsLoading] = useState(false);

  // API Tester state
  const [testPartner, setTestPartner] = useState<number | null>(null);
  const [testEndpoint, setTestEndpoint] = useState('/customer/5551234567/');
  const [testMethod, setTestMethod] = useState('GET');
  const [testPayload, setTestPayload] = useState('{}');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // Key generation state
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyModalPartner, setKeyModalPartner] = useState<Partner | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyType, setNewKeyType] = useState<'live' | 'test'>('test');
  const [generatedKey, setGeneratedKey] = useState<APIKey | null>(null);
  const [generating, setGenerating] = useState(false);

  // Docs state
  const [docs, setDocs] = useState<APIDocs | null>(null);

  // ═══════════════════════════════════════════════════════════════════════════════
  // DATA FETCHING
  // ═══════════════════════════════════════════════════════════════════════════════

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/partner-api/dashboard/');
      setDashboardData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const params: Record<string, string> = {};
      if (logsFilter.partner_id) params.partner_id = logsFilter.partner_id;
      if (logsFilter.status) params.status = logsFilter.status;
      if (logsFilter.endpoint) params.endpoint = logsFilter.endpoint;

      const response = await api.get('/admin/partner-api/logs/', { params });
      setLogs(response.data.logs);
    } catch (err: any) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [logsFilter]);

  const fetchDocs = useCallback(async () => {
    try {
      const response = await api.get('/admin/partner-api/docs/');
      setDocs(response.data);
    } catch (err: any) {
      console.error('Failed to fetch docs:', err);
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    try {
      setInventoryLoading(true);
      const params: Record<string, string> = {};
      if (inventoryFilter.partner_id) params.partner_id = inventoryFilter.partner_id;
      if (inventoryFilter.low_stock) params.low_stock = 'true';
      if (inventoryFilter.search) params.search = inventoryFilter.search;

      const response = await api.get('/admin/partner-api/inventory/', { params });
      setInventoryData(response.data);
    } catch (err: any) {
      console.error('Failed to fetch inventory:', err);
    } finally {
      setInventoryLoading(false);
    }
  }, [inventoryFilter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'inventory') {
      fetchInventory();
    } else if (activeTab === 'docs' && !docs) {
      fetchDocs();
    }
  }, [activeTab, fetchLogs, fetchInventory, fetchDocs, docs]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  const handleGenerateKey = async () => {
    if (!keyModalPartner) return;

    try {
      setGenerating(true);
      const response = await api.post('/admin/partner-api/keys/generate/', {
        customer_id: keyModalPartner.id,
        name: newKeyName || `API Key - ${new Date().toLocaleDateString()}`,
        key_type: newKeyType,
      });
      setGeneratedKey(response.data.api_key);
      // Refresh dashboard
      fetchDashboard();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleKey = async (keyId: number) => {
    try {
      await api.post(`/admin/partner-api/keys/${keyId}/toggle/`);
      fetchDashboard();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle key');
    }
  };

  const handleRunTest = async () => {
    if (!testPartner) {
      setError('Please select a partner first');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);

      let payload = {};
      try {
        payload = testPayload ? JSON.parse(testPayload) : {};
      } catch {
        setError('Invalid JSON payload');
        return;
      }

      const response = await api.post('/admin/partner-api/test/', {
        customer_id: testPartner,
        endpoint: testEndpoint,
        method: testMethod,
        payload,
      });

      setTestResult(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER DASHBOARD TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderDashboard = () => {
    if (!dashboardData) return null;

    const { partners, summary } = dashboardData;

    return (
      <div className="partner-api-dashboard">
        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon partners">
              <Building2 size={24} />
            </div>
            <div className="summary-content">
              <div className="summary-value">{summary.active_partners}</div>
              <div className="summary-label">Active Partners</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon keys">
              <Key size={24} />
            </div>
            <div className="summary-content">
              <div className="summary-value">{summary.active_keys}</div>
              <div className="summary-label">Active API Keys</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon requests">
              <Zap size={24} />
            </div>
            <div className="summary-content">
              <div className="summary-value">{summary.requests_24h.toLocaleString()}</div>
              <div className="summary-label">Requests (24h)</div>
            </div>
          </div>
          <div className="summary-card">
            <div className={`summary-icon ${summary.error_rate_24h > 5 ? 'error' : 'success'}`}>
              {summary.error_rate_24h > 5 ? <AlertTriangle size={24} /> : <CheckCircle size={24} />}
            </div>
            <div className="summary-content">
              <div className="summary-value">{summary.error_rate_24h}%</div>
              <div className="summary-label">Error Rate (24h)</div>
            </div>
          </div>
        </div>

        {/* Partners Table */}
        <div className="partners-section">
          <div className="section-header">
            <h2>Retail Partners</h2>
            <button className="btn-refresh" onClick={fetchDashboard}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          <div className="partners-table">
            <table>
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Location</th>
                  <th>API Keys</th>
                  <th>Total Requests</th>
                  <th>Last Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map(partner => (
                  <React.Fragment key={partner.id}>
                    <tr className={partner.has_api_access ? 'active' : 'inactive'}>
                      <td>
                        <button
                          className="partner-expand"
                          onClick={() => setExpandedPartner(
                            expandedPartner === partner.id ? null : partner.id
                          )}
                        >
                          {expandedPartner === partner.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <strong>{partner.name}</strong>
                      </td>
                      <td>{partner.city}, {partner.state}</td>
                      <td>
                        <span className={`badge ${partner.active_key_count > 0 ? 'badge-success' : 'badge-muted'}`}>
                          {partner.active_key_count} / {partner.key_count}
                        </span>
                      </td>
                      <td>{partner.total_requests.toLocaleString()}</td>
                      <td>
                        {partner.last_api_call ? (
                          <span className="last-activity">
                            <Clock size={14} />
                            {new Date(partner.last_api_call).toLocaleString()}
                          </span>
                        ) : (
                          <span className="no-activity">Never</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-generate"
                            title="Generate API Key"
                            onClick={() => {
                              setKeyModalPartner(partner);
                              setShowKeyModal(true);
                              setGeneratedKey(null);
                              setNewKeyName('');
                              setNewKeyType('test');
                            }}
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            className="btn-icon btn-test"
                            title="Test API"
                            onClick={() => {
                              setTestPartner(partner.id);
                              setActiveTab('tester');
                            }}
                          >
                            <TestTube size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedPartner === partner.id && (
                      <tr className="expanded-row">
                        <td colSpan={6}>
                          <div className="partner-details">
                            <p>API keys for this partner would be listed here.</p>
                            {/* Future: List partner's API keys with toggle/revoke options */}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER INVENTORY TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderInventory = () => {
    const summary = inventoryData?.summary;

    return (
      <div className="partner-api-inventory">
        {/* Summary Cards */}
        {summary && (
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-icon partners">
                <Building2 size={24} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{summary.partners_with_inventory}</div>
                <div className="summary-label">Partners with Inventory</div>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon keys">
                <Package size={24} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{summary.total_items}</div>
                <div className="summary-label">Total Products</div>
              </div>
            </div>
            <div className="summary-card">
              <div className={`summary-icon ${summary.total_low_stock > 0 ? 'error' : 'success'}`}>
                {summary.total_low_stock > 0 ? <AlertCircle size={24} /> : <CheckCircle size={24} />}
              </div>
              <div className="summary-content">
                <div className="summary-value">{summary.total_low_stock}</div>
                <div className="summary-label">Low Stock Items</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="inventory-filters">
          <select
            value={inventoryFilter.partner_id}
            onChange={(e) => setInventoryFilter({ ...inventoryFilter, partner_id: e.target.value })}
          >
            <option value="">All Partners</option>
            {dashboardData?.partners.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <label className="checkbox-filter">
            <input
              type="checkbox"
              checked={inventoryFilter.low_stock}
              onChange={(e) => setInventoryFilter({ ...inventoryFilter, low_stock: e.target.checked })}
            />
            Low Stock Only
          </label>
          <div className="search-input">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by product name or SKU..."
              value={inventorySearchInput}
              onChange={(e) => setInventorySearchInput(e.target.value)}
            />
          </div>
          <button className="btn-refresh" onClick={fetchInventory} disabled={inventoryLoading}>
            <RefreshCw size={16} className={inventoryLoading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>

        {/* Partner Inventory List */}
        <div className="inventory-partners">
          {inventoryData?.partners.map(partner => (
            <div key={partner.partner_id} className="inventory-partner-card">
              <div
                className="partner-header"
                onClick={() => setExpandedInventoryPartner(
                  expandedInventoryPartner === partner.partner_id ? null : partner.partner_id
                )}
              >
                <div className="partner-info">
                  <strong>{partner.partner_name}</strong>
                  <span className="partner-location">{partner.partner_city}, {partner.partner_state}</span>
                </div>
                <div className="partner-stats">
                  <span className="stat">
                    <Package size={14} />
                    {partner.total_items} items
                  </span>
                  {partner.low_stock_count > 0 && (
                    <span className="stat low-stock">
                      <AlertCircle size={14} />
                      {partner.low_stock_count} low
                    </span>
                  )}
                  {expandedInventoryPartner === partner.partner_id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {expandedInventoryPartner === partner.partner_id && (
                <div className="partner-inventory-items">
                  <table>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Status</th>
                        <th>Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partner.items.map(item => (
                        <tr key={item.id} className={item.is_low_stock ? 'low-stock-row' : ''}>
                          <td className="sku">{item.sku}</td>
                          <td>
                            <span className="product-name">{item.name}</span>
                            {item.feed_formula && (
                              <span className="formula-tag">{item.feed_formula.code}</span>
                            )}
                          </td>
                          <td className="quantity">{item.quantity}</td>
                          <td>
                            {item.is_low_stock ? (
                              <span className="status-badge status-low">Low Stock</span>
                            ) : (
                              <span className="status-badge status-ok">In Stock</span>
                            )}
                          </td>
                          <td className="timestamp">
                            {new Date(item.last_updated).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {partner.items.length === 0 && (
                        <tr>
                          <td colSpan={5} className="empty-state">
                            No inventory data for this partner.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          {(!inventoryData?.partners || inventoryData.partners.length === 0) && !inventoryLoading && (
            <div className="empty-inventory">
              <Package size={48} />
              <p>No inventory data found.</p>
              <p className="hint">Partners need to submit inventory via the API to see data here.</p>
            </div>
          )}

          {inventoryLoading && !inventoryData && (
            <div className="loading-state">
              <RefreshCw size={32} className="spinning" />
              <p>Loading inventory data...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER LOGS TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderLogs = () => (
    <div className="partner-api-logs">
      {/* Filters */}
      <div className="logs-filters">
        <select
          value={logsFilter.partner_id}
          onChange={(e) => setLogsFilter({ ...logsFilter, partner_id: e.target.value })}
        >
          <option value="">All Partners</option>
          {dashboardData?.partners.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={logsFilter.status}
          onChange={(e) => setLogsFilter({ ...logsFilter, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="success">Success (2xx)</option>
          <option value="error">Errors (4xx/5xx)</option>
        </select>
        <input
          type="text"
          placeholder="Filter by endpoint..."
          value={logsFilter.endpoint}
          onChange={(e) => setLogsFilter({ ...logsFilter, endpoint: e.target.value })}
        />
        <button className="btn-refresh" onClick={fetchLogs} disabled={logsLoading}>
          <RefreshCw size={16} className={logsLoading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Logs Table */}
      <div className="logs-table">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Partner</th>
              <th>Method</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Duration</th>
              <th>IP Address</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.id} className={log.response_status >= 400 ? 'error-row' : ''}>
                <td className="log-time">
                  {new Date(log.created_at).toLocaleString()}
                </td>
                <td>{log.partner_name}</td>
                <td>
                  <span className={`method-badge method-${log.method.toLowerCase()}`}>
                    {log.method}
                  </span>
                </td>
                <td className="log-endpoint">{log.endpoint}</td>
                <td>
                  <span className={`status-badge status-${Math.floor(log.response_status / 100)}xx`}>
                    {log.response_status}
                  </span>
                </td>
                <td>{log.response_time_ms}ms</td>
                <td className="log-ip">{log.ip_address}</td>
              </tr>
            ))}
            {logs.length === 0 && !logsLoading && (
              <tr>
                <td colSpan={7} className="empty-state">
                  No API logs found matching your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER API TESTER TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderTester = () => (
    <div className="partner-api-tester">
      <div className="tester-form">
        <h3>API Request Builder</h3>

        <div className="form-group">
          <label>Partner</label>
          <select
            value={testPartner || ''}
            onChange={(e) => setTestPartner(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Select a partner...</option>
            {dashboardData?.partners.filter(p => p.active_key_count > 0).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Method</label>
            <select value={testMethod} onChange={(e) => setTestMethod(e.target.value)}>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
          </div>
          <div className="form-group flex-grow">
            <label>Endpoint</label>
            <input
              type="text"
              value={testEndpoint}
              onChange={(e) => setTestEndpoint(e.target.value)}
              placeholder="/customer/5551234567/"
            />
          </div>
        </div>

        {testMethod === 'POST' && (
          <div className="form-group">
            <label>Request Body (JSON)</label>
            <textarea
              value={testPayload}
              onChange={(e) => setTestPayload(e.target.value)}
              rows={6}
              placeholder='{"customer_phone": "5551234567", "items": [...]}'
            />
          </div>
        )}

        <div className="quick-endpoints">
          <span>Quick Select:</span>
          <button onClick={() => { setTestMethod('GET'); setTestEndpoint('/customer/5551234567/'); }}>
            Customer Lookup
          </button>
          <button onClick={() => { setTestMethod('GET'); setTestEndpoint('/products/'); }}>
            Products List
          </button>
          <button onClick={() => {
            setTestMethod('POST');
            setTestEndpoint('/purchase/');
            setTestPayload(JSON.stringify({
              customer_phone: '5551234567',
              partner_transaction_id: 'TEST-' + Date.now(),
              purchase_date: new Date().toISOString().split('T')[0],
              items: [
                {
                  name: 'Premium Layer Pellet 40#',
                  sku: 'RBF-LAYER-P40',
                  brand: 'Red Bridge',
                  category: 'poultry',
                  weight_lbs: 40,
                  qty: 1,
                  price: 24.99
                }
              ],
              total: 24.99
            }, null, 2));
          }}>
            Purchase (Red Bridge)
          </button>
          <button onClick={() => {
            setTestMethod('POST');
            setTestEndpoint('/purchase/');
            setTestPayload(JSON.stringify({
              customer_phone: '5551234567',
              partner_transaction_id: 'TEST-' + Date.now(),
              purchase_date: new Date().toISOString().split('T')[0],
              items: [
                {
                  name: 'Diamond Naturals Adult Dog',
                  sku: 'DIA-DOG-40',
                  brand: 'Diamond',
                  category: 'dog',
                  weight_lbs: 40,
                  qty: 1,
                  price: 42.99
                }
              ],
              total: 42.99
            }, null, 2));
          }}>
            Purchase (Non-RB)
          </button>
          <button onClick={() => {
            setTestMethod('POST');
            setTestEndpoint('/purchase/');
            setTestPayload(JSON.stringify({
              customer_phone: '5551234567',
              partner_transaction_id: 'TEST-' + Date.now(),
              purchase_date: new Date().toISOString().split('T')[0],
              items: [
                {
                  name: 'Premium Layer Pellet 40#',
                  sku: 'RBF-LAYER-P40',
                  brand: 'Red Bridge',
                  category: 'poultry',
                  weight_lbs: 40,
                  qty: 2,
                  price: 24.99
                },
                {
                  name: 'All Flock Mash 40#',
                  sku: 'RBF-ALLFLOCK-M40',
                  brand: 'Red Bridge',
                  category: 'poultry',
                  weight_lbs: 40,
                  qty: 1,
                  price: 22.99
                },
                {
                  name: 'Diamond Naturals Adult Dog',
                  sku: 'DIA-DOG-40',
                  brand: 'Diamond',
                  category: 'dog',
                  weight_lbs: 40,
                  qty: 1,
                  price: 42.99
                }
              ],
              total: 115.96
            }, null, 2));
          }}>
            Purchase (Mixed)
          </button>
        </div>

        <button
          className="btn-primary btn-test-api"
          onClick={handleRunTest}
          disabled={testing || !testPartner}
        >
          {testing ? (
            <>
              <RefreshCw size={16} className="spinning" />
              Testing...
            </>
          ) : (
            <>
              <Send size={16} />
              Send Request
            </>
          )}
        </button>
      </div>

      {testResult && (
        <div className="test-result">
          <h3>
            {testResult.success ? (
              <><CheckCircle size={20} className="success" /> Request Successful</>
            ) : (
              <><XCircle size={20} className="error" /> Request Failed</>
            )}
          </h3>

          <div className="result-section">
            <h4>Request</h4>
            <pre>
              {testResult.request.method} {testResult.request.endpoint}
              {Object.keys(testResult.request.payload).length > 0 && (
                <>
                  {'\n\n'}
                  {JSON.stringify(testResult.request.payload, null, 2)}
                </>
              )}
            </pre>
          </div>

          <div className="result-section">
            <h4>Response ({testResult.response.duration_ms}ms)</h4>
            <div className="response-status">
              <span className={`status-badge status-${Math.floor(testResult.response.status / 100)}xx`}>
                {testResult.response.status}
              </span>
            </div>
            <pre>{JSON.stringify(testResult.response.body, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER DOCS TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderDocs = () => {
    if (!docs) return <div className="loading">Loading documentation...</div>;

    return (
      <div className="partner-api-docs">
        <div className="docs-header">
          <h2>{docs.title}</h2>
          <span className="version-badge">Version {docs.version}</span>
        </div>

        <div className="docs-section">
          <h3>Base URL</h3>
          <div className="code-block">
            <code>{docs.base_url}</code>
            <button className="btn-copy" onClick={() => copyToClipboard(docs.base_url)}>
              <Copy size={14} />
            </button>
          </div>
        </div>

        <div className="docs-section">
          <h3>Authentication</h3>
          <p>All requests must include your API key in the header:</p>
          <div className="code-block">
            <code>{docs.authentication.header}: {docs.authentication.format}</code>
          </div>
          <p className="hint">
            Keys starting with <code>rbf_live_</code> are for production.
            Keys starting with <code>rbf_test_</code> are for testing.
          </p>
        </div>

        <div className="docs-section">
          <h3>Endpoints</h3>
          {docs.endpoints.map((endpoint, idx) => (
            <div key={idx} className="endpoint-card">
              <div className="endpoint-header">
                <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <code className="endpoint-path">{endpoint.path}</code>
                <span className="endpoint-name">{endpoint.name}</span>
              </div>
              <p className="endpoint-description">{endpoint.description}</p>

              {endpoint.parameters && endpoint.parameters.length > 0 && (
                <div className="endpoint-params">
                  <h5>Parameters</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpoint.parameters.map((param, i) => (
                        <tr key={i}>
                          <td><code>{param.name}</code></td>
                          <td>{param.type}</td>
                          <td>{param.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {endpoint.request_example && (
                <div className="endpoint-example">
                  <h5>Request Body</h5>
                  <pre>{JSON.stringify(endpoint.request_example, null, 2)}</pre>
                </div>
              )}

              <div className="endpoint-example">
                <h5>Response Example</h5>
                <pre>{JSON.stringify(endpoint.response_example, null, 2)}</pre>
              </div>

              <div className="endpoint-curl">
                <h5>cURL Example</h5>
                <div className="code-block">
                  <code>{endpoint.curl_example}</code>
                  <button className="btn-copy" onClick={() => copyToClipboard(endpoint.curl_example)}>
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER KEY GENERATION MODAL
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderKeyModal = () => {
    if (!showKeyModal || !keyModalPartner) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowKeyModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowKeyModal(false)}>
            &times;
          </button>

          {!generatedKey ? (
            <>
              <h2>Generate API Key</h2>
              <p>Generate a new API key for <strong>{keyModalPartner.name}</strong></p>

              <div className="form-group">
                <label>Key Name</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., POS Integration Key"
                />
              </div>

              <div className="form-group">
                <label>Key Type</label>
                <div className="key-type-options">
                  <label className={`key-type-option ${newKeyType === 'test' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="keyType"
                      value="test"
                      checked={newKeyType === 'test'}
                      onChange={() => setNewKeyType('test')}
                    />
                    <span className="key-type-badge test">Test</span>
                    <span className="key-type-desc">For development & testing</span>
                  </label>
                  <label className={`key-type-option ${newKeyType === 'live' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="keyType"
                      value="live"
                      checked={newKeyType === 'live'}
                      onChange={() => setNewKeyType('live')}
                    />
                    <span className="key-type-badge live">Live</span>
                    <span className="key-type-desc">For production use</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowKeyModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleGenerateKey}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Key'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h2>
                <CheckCircle size={24} className="success" />
                API Key Generated
              </h2>

              <div className="generated-key-warning">
                <AlertTriangle size={20} />
                <p>Copy this key now! It will not be shown again.</p>
              </div>

              <div className="generated-key-display">
                <code>{generatedKey.key}</code>
                <button className="btn-copy" onClick={() => copyToClipboard(generatedKey.key || '')}>
                  <Copy size={16} />
                  Copy
                </button>
              </div>

              <div className="generated-key-info">
                <p><strong>Name:</strong> {generatedKey.name}</p>
                <p><strong>Type:</strong> <span className={`key-type-badge ${generatedKey.key_type}`}>{generatedKey.key_type}</span></p>
              </div>

              <div className="modal-actions">
                <button className="btn-primary" onClick={() => setShowKeyModal(false)}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════════

  if (loading && !dashboardData) {
    return (
      <div className="admin-partner-api">
        <div className="loading-state">
          <RefreshCw size={32} className="spinning" />
          <p>Loading Partner API Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-partner-api">
      <div className="page-header">
        <h1>Partner API Management</h1>
        <p>Monitor and manage retail partner POS integrations</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertTriangle size={16} />
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="api-tabs">
        <button
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <Activity size={18} />
          Dashboard
        </button>
        <button
          className={`tab ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          <Package size={18} />
          Inventory
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          <Clock size={18} />
          API Logs
        </button>
        <button
          className={`tab ${activeTab === 'tester' ? 'active' : ''}`}
          onClick={() => setActiveTab('tester')}
        >
          <TestTube size={18} />
          API Tester
        </button>
        <button
          className={`tab ${activeTab === 'docs' ? 'active' : ''}`}
          onClick={() => setActiveTab('docs')}
        >
          <FileText size={18} />
          Documentation
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'inventory' && renderInventory()}
        {activeTab === 'logs' && renderLogs()}
        {activeTab === 'tester' && renderTester()}
        {activeTab === 'docs' && renderDocs()}
      </div>

      {/* Key Generation Modal */}
      {renderKeyModal()}
    </div>
  );
};

export default AdminPartnerAPI;
